import React, { useState } from 'react';
import { Send, Link as LinkIcon, CheckCircle, ArrowLeft, Instagram, Facebook, Star } from 'lucide-react';

interface SubmissionProps {
  onNavigate: (page: string) => void;
}

const Submission: React.FC<SubmissionProps> = ({ onNavigate }) => {
  const [formState, setFormState] = useState({
    name: '',
    stageName: '',
    email: '',
    phoneNumber: '',
    genres: [] as string[],
    social_ig: '',
    social_fb: '',
    social_tiktok: '',
    social_other: '',
    music_spotify: '',
    music_soundcloud: '',
    music_apple: '',
    music_other: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const genreOptions = [
    { id: 'electronic', label: 'Electronic / Synthwave' },
    { id: 'rock', label: 'Rock / Alternative' },
    { id: 'pop', label: 'Pop / Mainstream' },
    { id: 'hiphop', label: 'Hip Hop / Rap' },
    { id: 'rnb', label: 'R&B / Soul' },
    { id: 'jazz', label: 'Jazz / Blues' },
    { id: 'country', label: 'Country / Folk' },
    { id: 'metal', label: 'Metal / Punk' },
    { id: 'classical', label: 'Classical / Instrumental' },
    { id: 'other', label: 'Other' }
  ];

  const toggleGenre = (genreId: string) => {
    setFormState((prev) => {
      const genres = prev.genres.includes(genreId)
        ? prev.genres.filter(g => g !== genreId)
        : [...prev.genres, genreId];
      return { ...prev, genres };
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setFormState({
      name: '', stageName: '', email: '', phoneNumber: '', genres: [],
      social_ig: '', social_fb: '', social_tiktok: '', social_other: '',
      music_spotify: '', music_soundcloud: '', music_apple: '', music_other: '',
      message: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // In a pure frontend app, Vercel requires environment variables to be prefixed with VITE_
      // so they can be injected into the built static files.
      const TELEGRAM_TOKEN = import.meta.env.VITE_TELEGRAM_TOKEN || "8859000868:AAHSzTz6DWHP1VxJsoevyIL0mVRV2rShVq8";
      const TELEGRAM_CHAT_ID = import.meta.env.VITE_TELEGRAM_CHAT_ID || "8961445557";

      const message = `
🌟 <b>New Artist Application</b> 🌟

<b>Name:</b> ${formState.name}
<b>Stage Name:</b> ${formState.stageName || 'N/A'}
<b>Email:</b> ${formState.email}
<b>Phone:</b> ${formState.phoneNumber}

<b>Genres:</b>
${formState.genres.length > 0 ? formState.genres.join(', ') : 'None selected'}

<b>Social Links:</b>
IG: ${formState.social_ig || 'N/A'}
FB: ${formState.social_fb || 'N/A'}
TikTok: ${formState.social_tiktok || 'N/A'}
Other: ${formState.social_other || 'N/A'}

<b>Music Links:</b>
Spotify: ${formState.music_spotify || 'N/A'}
SoundCloud: ${formState.music_soundcloud || 'N/A'}
Apple: ${formState.music_apple || 'N/A'}
Other: ${formState.music_other || 'N/A'}

<b>Additional Message:</b>
${formState.message || 'None'}
`;

      const response = await fetch(`https://api.telegram.org/bot${TELEGRAM_TOKEN}/sendMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error submitting your application. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const auroraStyle: React.CSSProperties = {
    backgroundImage: 'linear-gradient(to right, hsla(228, 51%, 70%, 1) 0%, hsla(27, 90%, 68%, 1) 45%, hsla(29, 100%, 49%, 1) 50%, hsla(27, 90%, 68%, 1) 55%, hsla(228, 51%, 70%, 1) 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
  };

  return (
    <section className="px-6 py-10 md:py-16 relative min-h-[80vh] flex flex-col items-center">
      {/* Background Tech Elements */}
      <div className="absolute top-20 left-10 w-24 h-24 border-l-2 border-t-2 border-slate-200 dark:border-slate-800 opacity-50 pointer-events-none"></div>
      <div className="absolute bottom-20 right-10 w-24 h-24 border-r-2 border-b-2 border-slate-200 dark:border-slate-800 opacity-50 pointer-events-none"></div>

      <div className="max-w-3xl w-full relative z-10">
        {/* Back Button */}
        <button 
          onClick={() => onNavigate('home')}
          className="mb-8 flex items-center gap-2 text-slate-500 hover:text-primary transition-colors font-mono text-[10px] uppercase tracking-[0.2em] group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>
        
        <div className="mb-10 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary font-mono text-[10px] tracking-widest uppercase mb-4">
                <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></span>
                SECURE A SLOT
            </div>
            <h1 
                className="text-4xl md:text-5xl font-bold uppercase font-display tracking-tighter animate-aurora"
                style={auroraStyle}
            >
              Artist Submission
            </h1>
        </div>

        {isSubmitted ? (
          <div className="bg-slate-50 dark:bg-slate-900/50 border border-green-500/30 p-12 rounded-sm text-center animate-in fade-in zoom-in duration-500">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center border border-green-500/50 shadow-[0_0_20px_rgba(34,197,94,0.2)]">
                <CheckCircle className="text-green-500 w-10 h-10" />
              </div>
            </div>
            <h3 className="text-2xl font-bold uppercase font-display text-slate-900 dark:text-white">
              Transmission Received
            </h3>
          </div>
        ) : (
          <form onSubmit={handleSubmit} autoComplete="off" className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 p-8 md:p-12 rounded-sm shadow-xl backdrop-blur-sm">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-3">
                <label className="text-xs font-mono font-bold uppercase tracking-wider text-slate-900 dark:text-white" htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                  className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm px-5 py-4 text-white placeholder-slate-400 focus:outline-none focus:border-primary transition-colors font-mono text-base"
                />
              </div>
              <div className="space-y-3">
                <label className="text-xs font-mono font-bold uppercase tracking-wider text-slate-900 dark:text-white" htmlFor="stageName">Stage Name</label>
                <input
                  type="text"
                  id="stageName"
                  name="stageName"
                  value={formState.stageName}
                  onChange={handleChange}
                  className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm px-5 py-4 text-white placeholder-slate-400 focus:outline-none focus:border-primary transition-colors font-mono text-base"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-3">
                <label className="text-xs font-mono font-bold uppercase tracking-wider text-slate-900 dark:text-white" htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formState.email}
                  onChange={handleChange}
                  className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm px-5 py-4 text-white placeholder-slate-400 focus:outline-none focus:border-primary transition-colors font-mono text-base"
                />
              </div>
              <div className="space-y-3">
                <label className="text-xs font-mono font-bold uppercase tracking-wider text-slate-900 dark:text-white" htmlFor="phoneNumber">Phone Number</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formState.phoneNumber}
                  onChange={handleChange}
                  className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm px-5 py-4 text-white placeholder-slate-400 focus:outline-none focus:border-primary transition-colors font-mono text-base"
                />
              </div>
            </div>

            <div className="space-y-3 mb-10">
              <label className="text-xs font-mono font-bold uppercase tracking-wider text-slate-900 dark:text-white">Genres (Select all that apply)</label>
              <div className="flex flex-wrap gap-3">
                {genreOptions.map(genre => (
                  <button
                    key={genre.id}
                    type="button"
                    onClick={() => toggleGenre(genre.id)}
                    className={`px-4 py-2 rounded-sm border font-mono text-sm transition-all ${
                      formState.genres.includes(genre.id)
                        ? 'bg-primary border-primary text-white shadow-[0_0_15px_rgba(249,115,22,0.3)]'
                        : 'bg-slate-900/50 dark:bg-slate-800/50 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    {genre.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-10">
                <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-4 border-b border-slate-200 dark:border-slate-800 pb-2">Social Media Handles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                        <Instagram className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input type="text" name="social_ig" value={formState.social_ig} onChange={handleChange} placeholder="Instagram URL" className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm pl-12 pr-5 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors font-mono text-sm" />
                    </div>
                    <div className="relative">
                        <Facebook className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input type="text" name="social_fb" value={formState.social_fb} onChange={handleChange} placeholder="Facebook URL" className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm pl-12 pr-5 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors font-mono text-sm" />
                    </div>
                    <div className="relative">
                        <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" width="18" height="18" viewBox="0 0 448 512" fill="currentColor"><path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/></svg>
                        <input type="text" name="social_tiktok" value={formState.social_tiktok} onChange={handleChange} placeholder="TikTok URL" className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm pl-12 pr-5 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors font-mono text-sm" />
                    </div>
                    <div className="relative">
                        <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input type="text" name="social_other" value={formState.social_other} onChange={handleChange} placeholder="Other Platform URL" className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm pl-12 pr-5 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors font-mono text-sm" />
                    </div>
                </div>
            </div>

            <div className="mb-10">
                <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-4 border-b border-slate-200 dark:border-slate-800 pb-2">Music Links</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                        <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" width="18" height="18" viewBox="0 0 496 512" fill="currentColor"><path d="M248 8C111.1 8 0 119.1 0 256s111.1 248 248 248 248-111.1 248-248S384.9 8 248 8zm100.7 364.9c-4.2 0-6.8-1.3-10.7-3.6-62.4-37.6-135-39.2-206.7-24.5-3.9 1-9 2.6-11.9 2.6-9.7 0-15.8-7.7-15.8-15.8 0-10.3 6.1-15.2 13.6-16.8 81.9-18.1 165.6-16.5 237 26.2 6.1 3.9 9.7 7.4 9.7 16.5s-7.1 15.4-15.2 15.4zm26.9-65.6c-5.2 0-8.7-2.3-12.3-4.2-74.4-41.4-169.1-46.6-237.7-28.8-4.5 1-9.4 2.3-12.6 2.3-12 0-20.1-8.1-20.1-20.1 0-13.6 7.8-18.4 15.8-20.4 80.6-21.7 186.8-15.5 272.1 32 6.5 3.6 11 8.7 11 18.7.2 11.4-8.5 20.5-16.2 20.5zm29-76.3c-6.8 0-10.7-3.2-14.9-5.5-84.5-45.6-212.1-51.8-283.5-30.7-5.5 1.6-11 2.9-15.5 2.9-14.9 0-25.2-10-25.2-25.2 0-15.2 9.1-22 17.5-24.3 84.5-24.6 226.9-17.5 323.5 34 8.1 4.5 13.6 11 13.6 23.6-.1 14.6-11 25.2-15.5 25.2z"/></svg>
                        <input type="url" name="music_spotify" value={formState.music_spotify} onChange={handleChange} placeholder="Spotify Artist URL" className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm pl-12 pr-5 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors font-mono text-sm" />
                    </div>
                    <div className="relative">
                        <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" width="18" height="18" viewBox="0 0 512 512" fill="currentColor"><path d="M111.4 242.1A104.9 104.9 0 0 0 10.5 347 104 104 0 0 0 114.5 451H401c59.6 0 100.5-43.2 100.5-101.4 0-54.8-44.5-98.3-99-98.3-2.1 0-4.2.1-6.2.2C384.8 174.1 322 121.4 246.6 121.4c-62.8 0-116.5 37-135.2 92.5V213.9zM72 388v-81.2l13-7.5v88.7zM42 376v-47l10-10v57zM16 352v-11l6-10v21zM97 400.5v-109l15-8.6v117.6zM130 409v-133l15-8.7v141.7zM162 414v-156l15-8.6v164.6zM195 422v-181l15-8.6v189.6zM227 427v-207l15-8.6v215.6zM260 429v-235l15-8.6v243.6zM292 429v-266l15-8.6v274.6zM325 427v-291l15-8.6v299.6zM357 421v-274.8l15-6.6V421z"/></svg>
                        <input type="url" name="music_soundcloud" value={formState.music_soundcloud} onChange={handleChange} placeholder="SoundCloud Profile URL" className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm pl-12 pr-5 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors font-mono text-sm" />
                    </div>
                    <div className="relative">
                        <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="18" viewBox="0 0 384 512" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
                        <input type="url" name="music_apple" value={formState.music_apple} onChange={handleChange} placeholder="Apple Music Artist URL" className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm pl-12 pr-5 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors font-mono text-sm" />
                    </div>
                    <div className="relative">
                        <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input type="url" name="music_other" value={formState.music_other} onChange={handleChange} placeholder="Other Platform URL" className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm pl-12 pr-5 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors font-mono text-sm" />
                    </div>
                </div>
            </div>

            <div className="space-y-3 mb-10">
              <label className="text-xs font-mono font-bold uppercase tracking-wider text-slate-900 dark:text-white" htmlFor="message">Message (Optional)</label>
              <textarea
                id="message"
                name="message"
                value={formState.message}
                onChange={handleChange}
                rows={5}
                className="w-full bg-slate-900/80 dark:bg-black/50 border border-slate-200 dark:border-slate-700 rounded-sm px-5 py-4 text-white placeholder-slate-400 focus:outline-none focus:border-primary transition-colors font-mono text-base resize-none"
              />
            </div>

            <button 
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-primary hover:bg-orange-600 disabled:bg-slate-600 text-white font-mono font-bold py-5 px-8 flex items-center justify-center gap-3 transition-all group rounded-sm shadow-[0_4px_14px_0_rgba(249,115,22,0.39)] hover:shadow-[0_6px_20px_rgba(249,115,22,0.23)] hover:-translate-y-1 ${isSubmitting ? 'cursor-wait opacity-80' : ''}`}
            >
              {isSubmitting ? (
                 <>
                   <Star className="animate-spin h-4 w-4 text-white" />
                   UPLOADING DATA...
                 </>
              ) : (
                 <>
                    SUBMIT APPLICATION
                    <Send size={18} className="group-hover:translate-x-1 transition-transform" />
                 </>
              )}
            </button>

          </form>
        )}
      </div>
    </section>
  );
};

export default Submission;